import React, { useRef, useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import "../styles/Services.css";

const useVisible = (threshold = 0.1) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

const SERVICES = [
  {
    tag: "<Web.Development>",
    title: "Web Development",
    desc: "Fast, modern, and fully responsive websites built with clean architecture. Optimised for performance, usability, and conversion — every line of code has a purpose.",
    chips: ["ReactJS", "Node.js", "WordPress", "PHP", "REST API"],
    accent: "blue",
  },
  {
    tag: "<Mobile.Apps>",
    title: "Mobile App Development",
    desc: "Cross-platform and native mobile applications that deliver seamless user experiences on iOS and Android — from MVP to production-ready product.",
    chips: ["React Native", "iOS", "Android", "Firebase", "App Store"],
    accent: "orange",
  },
  {
    tag: "<Custom.Systems>",
    title: "Custom Systems & Dashboards",
    desc: "Bespoke business systems, admin dashboards, CRMs, and internal tools built exactly to your workflow — replacing spreadsheets with powerful, scalable software.",
    chips: ["Custom CRM", "ERP", "Panels", "Analytics", "Automation"],
    accent: "blue",
  },
  {
    tag: "<SaaS.Development>",
    title: "SaaS Product Development",
    desc: "End-to-end SaaS product development — from architecture and design to deployment, billing integration, and user management. We help you launch and scale.",
    chips: ["Subscription", "Multi-tenant", "Stripe", "Auth", "Cloud"],
    accent: "orange",
  },
  {
    tag: "<AI.Integration>",
    title: "AI Integration & Automation",
    desc: "Embed intelligent automation into your business — AI-powered chatbots, document processing, workflow automation, and custom LLM integrations that save time and cost.",
    chips: ["OpenAI", "LangChain", "Chatbots", "Workflows", "n8n"],
    accent: "blue",
  },
  {
    tag: "<SEO.Speed.optimization>",
    title: "SEO & Digital Growth",
    desc: "Improve search rankings and attract qualified organic traffic. We optimise site structure, content strategy, and technical SEO for measurable, long-term results.",
    chips: ["On-Page SEO", "Technical SEO", "Core Web Vitals", "Analytics"],
    accent: "orange",
  },
  {
    tag: "<Website.Maintenance>",
    title: "Website Maintenance",
    desc: "Keep your website secure, updated, and performing at its best. Regular updates, automated backups, uptime monitoring, and proactive performance checks.",
    chips: ["Updates", "Backups", "Monitoring", "Security", "Speed"],
    accent: "blue",
  },
  {
    tag: "<Hosting.Support>",
    title: "Hosting, Domains & Support",
    desc: "Reliable hosting infrastructure and domain management with expert support. We keep your website online, stable, and fully accessible — 24/7.",
    chips: ["Cloud Hosting", "SSL", "Domains", "CDN", "24/7 Support"],
    accent: "orange",
  },
];

function ServiceCard({ svc, index, visible }) {
  return (
    <div
      className={`svc-card svc-card--${svc.accent} ${visible ? "svc-card--in" : ""}`}
      style={{ transitionDelay: `${(index % 4) * 100}ms` }}
    >
      <div className="svc-card-top">
        <span className="svc-tag">{svc.tag}</span>
        <span className="svc-num">{String(index + 1).padStart(2, "0")}</span>
      </div>
      <h3 className="svc-title">{svc.title}</h3>
      <p className="svc-desc">{svc.desc}</p>
      <div className="svc-chips">
        {svc.chips.map((c) => (
          <span key={c} className="svc-chip">{c}</span>
        ))}
      </div>
      <div className="svc-line" aria-hidden="true" />
    </div>
  );
}

function Services() {
  const [headerRef, headerVis] = useVisible(0.1);
  const [gridRef, gridVis] = useVisible(0.05);

  return (
    <>
      <Helmet>
        <title>Services | Codix Tech — Web, Mobile, SaaS & AI Development</title>
        <meta
          name="description"
          content="Codix Tech offers web development, mobile apps, SaaS platforms, AI integration, SEO, and hosting services. Full-service digital agency in Lebanon."
        />
        <meta
          name="keywords"
          content="Web Development Lebanon, Mobile App Development, SaaS Development, AI Integration, SEO Lebanon, Custom Systems, Codix Tech"
        />
        <meta name="author" content="Codix Tech" />
      </Helmet>

      <section id="services" className="services-section">
        <div className="about-rule" aria-hidden="true" />
        <div className="services-container">

          {/* Header */}
          <div ref={headerRef} className={`svc-header ${headerVis ? "svc-header--in" : ""}`}>
            <span className="cdx-label">// 02 — what we do</span>
            <h2 className="cdx-title">
             &lt;<span className="orange"> Our  </span>Services<span className="orange">/</span>&gt;
            </h2>
            <div className="cdx-rule" />
            <p className="svc-subtitle">
              From a simple landing page to a full AI-powered SaaS platform — we cover
              the entire digital product lifecycle under one roof.
            </p>
          </div>

          {/* Grid */}
          <div ref={gridRef} className="svc-grid">
            {SERVICES.map((svc, i) => (
              <ServiceCard key={svc.tag} svc={svc} index={i} visible={gridVis} />
            ))}
          </div>

          {/* CTA */}
          <div className={`svc-cta ${headerVis ? "svc-cta--in" : ""}`}>
            <p>Don't see exactly what you need? We build custom solutions for any challenge.</p>
            <a
              href="https://book.codixlb.com"
              target="_blank"
              rel="noopener noreferrer"
              className="cdx-btn-primary"
            >
              Book a Free Consultation
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

export default Services;
